import { CartLinesUpdateEvent } from '@shopify/events';

const CART_ADD_URL_PATTERN = /\/cart\/add(?:\.js)?(?:\?|$)/;
const GPO_CLONE_SELECTOR = '.gpo-clone-atc-button, .gpo-clone-stickyAtc-button';
const SYNC_DEBOUNCE_MS = 50;
const SYNC_COOLDOWN_MS = 2500;

/** @type {ReturnType<typeof setTimeout> | null} */
let syncTimer = null;
/** @type {number} */
let lastSyncAt = 0;
let fetchPatched = false;

/**
 * Globo Product Options clones the add-to-cart button and posts its own
 * `/cart/add.js` request when an add-on product is selected. Horizon never
 * sees that add, so the cart drawer and count stay stale until reload.
 */
function initGloboCartSync() {
  document.addEventListener('click', handleGpoCloneClick, true);
  document.addEventListener('gpoAddedToCart', () => scheduleHorizonCartSync());
  patchFetchForGpoAddonAdds();
}

/**
 * @param {Event} event
 */
function handleGpoCloneClick(event) {
  const eventTarget = event.target;
  const target =
    eventTarget instanceof Element ? eventTarget : eventTarget instanceof Node ? eventTarget.parentElement : null;
  if (!target) return;

  const clone = target.closest(GPO_CLONE_SELECTOR);
  if (!clone) return;

  playAddToCartAnimation(clone);
}

/**
 * Globo marks the theme add-to-cart button as a clone and intercepts submit,
 * so Horizon's native handleClick animation does not run. Replay the same
 * fly-to-cart + added-state animation from the visible button.
 *
 * @param {Element} clone
 */
function playAddToCartAnimation(clone) {
  const addToCart = document.querySelector('product-form-component add-to-cart-component');
  const nativeButton = addToCart?.querySelector('[ref="addToCartButton"]');
  const sticky = clone.closest('sticky-add-to-cart') ?? document.querySelector('sticky-add-to-cart');
  const isSticky = Boolean(
    clone.classList.contains('gpo-clone-stickyAtc-button') || clone.closest('sticky-add-to-cart')
  );
  const sourceButton = clone.matches('button') ? clone : clone.querySelector('button') ?? clone;

  // Prevent Horizon's native handleClick from playing a second fly-to-cart on
  // the same click. Globo keeps the original button and intercepts submit.
  if (nativeButton instanceof HTMLElement) {
    nativeButton.dataset.puppet = 'true';
    window.setTimeout(() => {
      nativeButton.dataset.puppet = 'false';
    }, 0);
  }

  addToCart?.animateAddToCart?.();
  markButtonAdded(sourceButton);

  const animationEnabled = addToCart?.dataset.addToCartAnimation === 'true';
  if (!animationEnabled) return;

  const cartIcon = document.querySelector('.header-actions__cart-icon');
  if (!cartIcon) return;

  const stickyImage = /** @type {HTMLImageElement | null} */ (sticky?.querySelector('[ref="productImage"]'));
  const image = isSticky && stickyImage?.src ? stickyImage.src : addToCart?.dataset.productVariantMedia;
  if (!image) return;

  const flyToCartElement = /** @type {HTMLElement & {source: Element, destination: Element, useSourceSize: string | boolean}} */ (
    document.createElement('fly-to-cart')
  );
  flyToCartElement.classList.add(isSticky ? 'fly-to-cart--sticky' : 'fly-to-cart--main');
  flyToCartElement.style.setProperty('background-image', `url(${image})`);

  if (isSticky && stickyImage) {
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = stickyImage;
  } else {
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = sourceButton;
  }

  flyToCartElement.destination = cartIcon;
  document.body.appendChild(flyToCartElement);
}

/**
 * @param {Element} button
 */
function markButtonAdded(button) {
  if (!(button instanceof HTMLElement)) return;

  button.dataset.added = 'true';
  window.setTimeout(() => {
    button.removeAttribute('data-added');
  }, 800);
}

function patchFetchForGpoAddonAdds() {
  if (fetchPatched || typeof window.fetch !== 'function') return;
  fetchPatched = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);

    if (isGpoAddonCartAdd(input) && response.ok) {
      scheduleHorizonCartSync();
    }

    return response;
  };
}

/**
 * @param {RequestInfo | URL} input
 */
function getRequestUrl(input) {
  if (typeof input === 'string') return input;
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
  if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
  return String(input?.url ?? input ?? '');
}

/**
 * @param {RequestInfo | URL} input
 */
function isGpoAddonCartAdd(input) {
  if (!CART_ADD_URL_PATTERN.test(getRequestUrl(input))) return false;

  return Boolean(document.querySelector(GPO_CLONE_SELECTOR));
}

function scheduleHorizonCartSync() {
  if (Date.now() - lastSyncAt < SYNC_COOLDOWN_MS) return;

  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncTimer = null;
    syncHorizonCart();
  }, SYNC_DEBOUNCE_MS);
}

async function syncHorizonCart() {
  lastSyncAt = Date.now();
  const cartUrl = window.Theme?.routes?.cart_url || '/cart';

  try {
    const cart = await fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    const source = document.querySelector('product-form-component') ?? document;
    const lines = Array.isArray(cart.items)
      ? cart.items.map((item) => ({
          merchandiseId: String(item.variant_id),
          quantity: item.quantity,
        }))
      : [];

    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferredEventPromise.promise,
      })
    );

    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        source: 'globo-product-options',
        didError: false,
      },
    });
  } catch (error) {
    lastSyncAt = 0;
    console.warn('[globo-cart-sync] Failed to refresh Horizon cart', error);
  }
}

initGloboCartSync();
